import React, { useState } from 'react';

/**
 * AI Service Status Dashboard
 * Monitor AI tools status and emergency controls
 */
const AIServiceStatus = () => {
  const [emergencyStop, setEmergencyStop] = useState(false);
  const [services, setServices] = useState([
    {
      id: 'ai_staff',
      name: 'AI Staff',
      description: 'Content generation & customer service',
      status: 'operational',
      usage: { current: 847, limit: 10000, unit: 'requests' },
      lastUsed: '2 minutes ago',
      icon: '🤖',
      color: 'blue'
    },
    {
      id: 'ai_socials',
      name: 'AI Socials',
      description: 'Social media content',
      status: 'operational',
      usage: { current: 234, limit: 5000, unit: 'posts' },
      lastUsed: '15 minutes ago',
      icon: '📱',
      color: 'purple'
    },
    {
      id: 'artistly',
      name: 'Artistly',
      description: 'AI image generation',
      status: 'operational',
      usage: { current: 56, limit: 500, unit: 'images' },
      lastUsed: '1 hour ago',
      icon: '🎨',
      color: 'pink'
    },
    {
      id: 'voiceclone',
      name: 'VoiceClone.ai',
      description: 'AI voice synthesis',
      status: 'operational',
      usage: { current: 12, limit: 100, unit: 'minutes' },
      lastUsed: '3 hours ago',
      icon: '🎙️',
      color: 'green'
    },
    {
      id: 'video_express',
      name: 'Video Express',
      description: 'AI video creation',
      status: 'operational',
      usage: { current: 8, limit: 50, unit: 'videos' },
      lastUsed: '1 day ago',
      icon: '🎬',
      color: 'orange'
    },
    {
      id: 'ai_reputor',
      name: 'AI Reputor',
      description: 'Review monitoring & responses',
      status: 'operational',
      usage: { current: 145, limit: 1000, unit: 'reviews' },
      lastUsed: '5 minutes ago',
      icon: '⭐',
      color: 'yellow'
    }
  ]);

  const handleEmergencyStop = () => {
    if (!emergencyStop) {
      if (window.confirm('⚠️ EMERGENCY STOP\n\nThis will immediately halt ALL AI content generation.\n\nAre you sure?')) {
        setEmergencyStop(true);
        alert('🛑 EMERGENCY STOP ACTIVATED\n\nAll AI content generation has been halted.\n\nClick "Resume Automation" when ready to continue.');
      }
    } else {
      if (window.confirm('Resume AI automation?\n\nAI services will be re-enabled.')) {
        setEmergencyStop(false);
        alert('✅ Automation resumed\n\nAI services are now operational.');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'limited':
        return 'bg-yellow-100 text-yellow-800';
      case 'offline':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getUsagePercentage = (service) => {
    return (service.usage.current / service.usage.limit) * 100;
  };

  const getUsageColor = (percentage) => {
    if (percentage < 50) return 'bg-green-500';
    if (percentage < 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getServiceColor = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600',
      purple: 'from-purple-500 to-purple-600',
      pink: 'from-pink-500 to-pink-600',
      green: 'from-green-500 to-green-600',
      orange: 'from-orange-500 to-orange-600',
      yellow: 'from-yellow-500 to-yellow-600'
    };
    return colors[color] || 'from-gray-500 to-gray-600';
  };

  return (
    <div className="space-y-6">
      {/* Emergency Stop Section */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            🎛️ AI Automation Controls
          </h2>
          <p className="text-gray-600">
            Emergency controls and system status monitoring
          </p>
        </div>

        {/* Emergency Stop Button */}
        <div className={`rounded-xl p-6 mb-4 ${emergencyStop ? 'bg-red-50 border-2 border-red-300' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {emergencyStop ? '🛑 Emergency Stop Active' : '⚡ Automation Status'}
              </h3>
              <p className="text-sm text-gray-600">
                {emergencyStop 
                  ? 'All AI content generation is currently HALTED' 
                  : 'AI services operational - Manual approval required for all content'}
              </p>
            </div>
            <button
              onClick={handleEmergencyStop}
              className={`px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg ${
                emergencyStop
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
              }`}
            >
              {emergencyStop ? '▶️ Resume Automation' : '🛑 EMERGENCY STOP'}
            </button>
          </div>
        </div>

        {/* System Mode Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Operation Mode</div>
            <div className="text-lg font-bold text-blue-600">
              {emergencyStop ? 'STOPPED' : 'Manual Mode'}
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Auto-Publishing</div>
            <div className="text-lg font-bold text-red-600">DISABLED</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Approval Required</div>
            <div className="text-lg font-bold text-green-600">YES - 100%</div>
          </div>
        </div>
      </div>

      {/* Service Status Cards */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          📊 AI Services Status
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((service) => {
            const usagePercentage = getUsagePercentage(service);
            return (
              <div
                key={service.id}
                className={`rounded-xl p-5 bg-gradient-to-br ${getServiceColor(service.color)} text-white ${
                  emergencyStop ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-4xl">{service.icon}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    emergencyStop 
                      ? 'bg-red-500' 
                      : getStatusColor(service.status).replace('text-', 'bg-').replace('100', '500')
                  }`}>
                    {emergencyStop ? 'STOPPED' : service.status.toUpperCase()}
                  </span>
                </div>

                <h3 className="text-lg font-bold mb-1">{service.name}</h3>
                <p className="text-sm opacity-90 mb-4">{service.description}</p>

                {/* Usage Bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span>Usage</span>
                    <span>{service.usage.current} / {service.usage.limit} {service.usage.unit}</span>
                  </div>
                  <div className="w-full bg-white bg-opacity-30 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${getUsageColor(usagePercentage)} transition-all`}
                      style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="text-xs opacity-75">
                  Last used: {service.lastUsed}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Usage Summary */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          📈 Usage Summary
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-gray-900">
              {services.reduce((sum, s) => sum + s.usage.current, 0)}
            </div>
            <div className="text-sm text-gray-600">Total API Calls</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-green-600">
              {services.filter(s => s.status === 'operational').length}
            </div>
            <div className="text-sm text-gray-600">Services Online</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600">0</div>
            <div className="text-sm text-gray-600">Auto-Published</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-purple-600">100%</div>
            <div className="text-sm text-gray-600">Human Review Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIServiceStatus;
